/**
 * Buses and commercial passenger vehicles, as this marketplace sees them.
 *
 * The category rides on the existing listing infrastructure rather than
 * beside it. A coach is a listing like any other: same search index, same
 * Garage, same buyer matching, same detail page. What differs is a handful of
 * public-data keys that a car does not carry, and this module is the single
 * place that knows their names and what the values mean.
 *
 * **Where these fields are defined.** Not here. `mergeConfig` resolves listing
 * fields from the hosted Console asset and discards the local array —
 * `mergeDefaultTypesAndFieldsForDebugging(false)` in src/util/configHelpers.js
 * is hardcoded false, so src/config/configListing.js is debug-only and
 * anything added there is dead in production. The operator defines these
 * fields in Console; docs/console-fields-commercial-passenger.md is the exact
 * specification. This module reads what Console produces, which is why every
 * accessor here tolerates the field being absent: a listing created before the
 * Console fields existed must still render, not throw.
 *
 * Seating capacity and wheelchair accessibility are the two that matter most
 * to the buyers this category is for. A school division needs a seat count it
 * can trust; a band office arranging community transport needs to know whether
 * a lift is fitted. Both are answered as "unknown" rather than guessed, since
 * a wrong accessibility answer wastes a trip across a province.
 */

import { constructQueryParamName } from './search.js';

/** The Console listing type that marks a commercial passenger vehicle. */
export const COMMERCIAL_PASSENGER_LISTING_TYPE = 'commercial-passenger';

/**
 * Body types in this category. Kept separate from the car `bodyType` enum so
 * neither list has to carry the other's options — a buyer filtering for an
 * SUV should never be offered "transit bus".
 */
export const BUS_TYPES = {
  MOTORCOACH: 'motorcoach',
  SCHOOL_BUS: 'school-bus',
  TRANSIT_BUS: 'transit-bus',
  SHUTTLE_BUS: 'shuttle-bus',
  ACCESSIBLE_BUS: 'accessible-bus',
  PASSENGER_VAN: 'passenger-van',
};

export const BUS_TYPE_LABELS = {
  [BUS_TYPES.MOTORCOACH]: 'Motorcoach (MCI, Prevost)',
  [BUS_TYPES.SCHOOL_BUS]: 'School bus',
  [BUS_TYPES.TRANSIT_BUS]: 'Transit bus',
  [BUS_TYPES.SHUTTLE_BUS]: 'Shuttle bus',
  [BUS_TYPES.ACCESSIBLE_BUS]: 'Accessible bus',
  [BUS_TYPES.PASSENGER_VAN]: 'Passenger van',
};

/**
 * Public-data keys. Console must use these exact strings — they are the
 * contract between the operator's configuration and this code.
 */
export const FIELD = {
  busType: 'busType',
  seatingCapacity: 'seatingCapacity',
  wheelchairAccessible: 'wheelchairAccessible',
  wheelchairPositions: 'wheelchairPositions',
  engine: 'engine',
  transmission: 'transmission',
  odometerKm: 'odometerKm',
  serviceHistory: 'serviceHistory',
  lengthFeet: 'lengthFeet',
  heightInches: 'heightInches',
  gvwrKg: 'gvwrKg',
  commercialEquipment: 'commercialEquipment',
};

/**
 * Coach and bus manufacturers.
 *
 * These are makes, not body types. MCI and Prevost build motorcoaches; Blue
 * Bird, Thomas and IC build school buses; New Flyer and Nova build transit.
 * They read from the same `make` key a car uses, so a buyer searching by
 * manufacturer uses one field across the whole marketplace rather than two.
 *
 * The car `make` enum in Console does not carry these, and this list does not
 * put them there — Console must add them, or leave `make` free text for this
 * listing type. Until it does, `busMakeOf` reports null and the spec row is
 * omitted, which is the same honest-blank every other field uses. That is
 * recorded in docs/console-fields-commercial-passenger.md rather than worked
 * around here, because a second make field would be the duplicate system this
 * category is specifically meant to avoid.
 */
export const BUS_MAKES = {
  mci: 'MCI (Motor Coach Industries)',
  prevost: 'Prevost',
  'van-hool': 'Van Hool',
  setra: 'Setra',
  temsa: 'TEMSA',
  'blue-bird': 'Blue Bird',
  thomas: 'Thomas Built',
  ic: 'IC Bus',
  'new-flyer': 'New Flyer',
  nova: 'Nova Bus',
  gillig: 'GILLIG',
  'el-dorado': 'ElDorado',
  glaval: 'Glaval',
  girardin: 'Girardin',
  ford: 'Ford',
  'mercedes-benz': 'Mercedes-Benz',
  other: 'Other',
};

export const ACCESSIBILITY = {
  /** A lift or ramp is fitted and the seller has said so. */
  EQUIPPED: 'equipped',
  /** The seller has said it is not fitted. */
  NOT_EQUIPPED: 'not-equipped',
  /** Nobody has said. Never shown as either of the above. */
  UNKNOWN: 'unknown',
};

const publicData = listing => listing?.attributes?.publicData || {};

/** @returns {Boolean} whether this listing is in the category at all. */
export const isCommercialPassenger = listing =>
  publicData(listing).listingType === COMMERCIAL_PASSENGER_LISTING_TYPE ||
  Object.values(BUS_TYPES).includes(publicData(listing)[FIELD.busType]);

/** @returns {String|null} a known bus type, or null — never a guess. */
export const busTypeOf = listing => {
  const value = publicData(listing)[FIELD.busType];
  return Object.values(BUS_TYPES).includes(value) ? value : null;
};

/**
 * The manufacturer, when it is one this category recognises.
 *
 * Matched case-insensitively against the key, because Console enums and
 * hand-entered values disagree about capitalisation and a buyer searching
 * "MCI" should not miss a listing stored as "mci".
 *
 * @returns {String|null} a label, or null — never the raw value echoed back
 */
export const busMakeOf = listing => {
  const raw = publicData(listing).make;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const key = raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');
  return BUS_MAKES[key] || null;
};

export const busTypeLabel = listing => {
  const type = busTypeOf(listing);
  return type ? BUS_TYPE_LABELS[type] : null;
};

/**
 * The largest seat count this module will report.
 *
 * A double-decker or articulated bus reaches about 130, so 200 leaves room
 * while still refusing a data-entry error. Named and exported because the
 * search upper bound has to be this same number: a smaller one makes a real
 * bus unfindable, and a larger one promises listings `seatingCapacityOf`
 * would report as unknown anyway.
 */
export const MAX_REPORTED_SEATS = 200;

/**
 * Seats, as a number, or null.
 *
 * Rejects anything that is not a positive integer rather than coercing it. A
 * seat count is a number a school division plans routes around; `"54 + driver"`
 * parsed as 54 would be a quiet lie about capacity, so it is refused and shown
 * as unknown instead.
 */
export const seatingCapacityOf = listing => {
  const raw = publicData(listing)[FIELD.seatingCapacity];
  if (typeof raw !== 'number' || !Number.isInteger(raw) || raw <= 0) {
    return null;
  }
  return raw <= MAX_REPORTED_SEATS ? raw : null;
};

/**
 * Accessibility, three-valued on purpose.
 *
 * A missing field is UNKNOWN, never NOT_EQUIPPED. The difference matters to
 * the buyer who needs a lift: "we don't know" prompts a question, "not
 * equipped" ends the search. Only an explicit `false` means not equipped.
 */
export const accessibilityOf = listing => {
  const raw = publicData(listing)[FIELD.wheelchairAccessible];
  if (raw === true) return ACCESSIBILITY.EQUIPPED;
  if (raw === false) return ACCESSIBILITY.NOT_EQUIPPED;
  return ACCESSIBILITY.UNKNOWN;
};

/** @returns {Number|null} securement positions, when stated and sane. */
export const wheelchairPositionsOf = listing => {
  const raw = publicData(listing)[FIELD.wheelchairPositions];
  if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < 0 || raw > 20) {
    return null;
  }
  // Positions without a lift is a contradiction; report the field, not a story.
  return raw;
};

/**
 * The spec rows a detail page shows, already filtered to what is actually
 * known. Returning only present values is what keeps a sparse listing from
 * rendering a column of "—".
 *
 * @returns {Array<{key: String, label: String, value: String}>}
 */
export const specRowsOf = listing => {
  const data = publicData(listing);
  const rows = [];
  const push = (key, label, value) => {
    if (value !== null && value !== undefined && value !== '') rows.push({ key, label, value });
  };

  push('make', 'Make', busMakeOf(listing));
  push(FIELD.busType, 'Type', busTypeLabel(listing));
  const seats = seatingCapacityOf(listing);
  push(FIELD.seatingCapacity, 'Seating capacity', seats === null ? null : `${seats}`);

  const access = accessibilityOf(listing);
  if (access !== ACCESSIBILITY.UNKNOWN) {
    const positions = wheelchairPositionsOf(listing);
    push(
      FIELD.wheelchairAccessible,
      'Wheelchair accessible',
      access === ACCESSIBILITY.EQUIPPED
        ? positions
          ? `Yes — ${positions} securement position${positions === 1 ? '' : 's'}`
          : 'Yes'
        : 'No'
    );
  }

  push(FIELD.engine, 'Engine', data[FIELD.engine]);
  push(FIELD.transmission, 'Transmission', data[FIELD.transmission]);
  const km = data[FIELD.odometerKm];
  push(
    FIELD.odometerKm,
    'Odometer',
    typeof km === 'number' && km >= 0 ? `${km.toLocaleString('en-CA')} km` : null
  );
  const length = data[FIELD.lengthFeet];
  push(
    FIELD.lengthFeet,
    'Length',
    typeof length === 'number' && length > 0 ? `${length} ft` : null
  );
  const height = data[FIELD.heightInches];
  push(
    FIELD.heightInches,
    'Height',
    typeof height === 'number' && height > 0 ? `${height} in` : null
  );
  const gvwr = data[FIELD.gvwrKg];
  push(
    FIELD.gvwrKg,
    'GVWR',
    typeof gvwr === 'number' && gvwr > 0 ? `${gvwr.toLocaleString('en-CA')} kg` : null
  );

  const equipment = data[FIELD.commercialEquipment];
  push(
    FIELD.commercialEquipment,
    'Commercial equipment',
    Array.isArray(equipment) && equipment.length ? equipment.join(', ') : null
  );

  const history = data[FIELD.serviceHistory];
  push(FIELD.serviceHistory, 'Service history', typeof history === 'string' ? history : null);

  return rows;
};

/**
 * Search parameters for this category, built with the same
 * `constructQueryParamName` every other filter uses so there is one convention
 * for query strings rather than a second one for buses.
 *
 * `minSeats` becomes an open-ended range, which is what a buyer means: a
 * division needing 40 seats will take 48, never 32.
 *
 * @param {Object} opts
 * @param {Array<String>} opts.busTypes       any of BUS_TYPES
 * @param {Number} opts.minSeats              lower bound, inclusive
 * @param {Boolean} opts.wheelchairAccessible true to require a lift or ramp
 * @returns {Object} query params, ready to merge into a search URL
 */
export const commercialPassengerSearchParams = ({
  busTypes = [],
  minSeats = null,
  wheelchairAccessible = false,
} = {}) => {
  const params = {
    pub_listingType: COMMERCIAL_PASSENGER_LISTING_TYPE,
  };

  const valid = busTypes.filter(t => Object.values(BUS_TYPES).includes(t));
  if (valid.length) {
    params[constructQueryParamName(FIELD.busType, 'public')] = valid.join(',');
  }

  if (Number.isInteger(minSeats) && minSeats > 0) {
    // Bounded, not open-ended. `${minSeats},` is valid Sharetribe range syntax,
    // but the parameter never reaches Sharetribe: SearchPage's
    // prepareIntegerRangeParam parses both endpoints, and
    // `Number.parseInt('', 10) + 1` is NaN, which fails its Number.isInteger
    // check and makes it drop the property entirely. The URL looked correct and
    // the seat filter quietly did nothing — the same shape of fault as the
    // postal search returning every listing in Canada.
    //
    // The upper bound is MAX_REPORTED_SEATS, the ceiling seatingCapacityOf
    // applies, so the filter asks for exactly the range the reader is prepared
    // to report.
    params[
      constructQueryParamName(FIELD.seatingCapacity, 'public')
    ] = `${minSeats},${MAX_REPORTED_SEATS}`;
  }

  // Only ever filter *for* accessibility. Filtering for its absence would hide
  // listings whose seller simply left the field blank, which is most of them.
  if (wheelchairAccessible === true) {
    params[constructQueryParamName(FIELD.wheelchairAccessible, 'public')] = 'true';
  }

  return params;
};
